import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import SmartDPHotelDescription from "app/pages/SmartDP/Quote/HotelDescription/SmartDPHotelDescription";
import { bindActionCreators } from "redux";
import { fetchSDPAccommodationDescription } from "app/pages/SmartDP/smartDPActionCreators";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { parseQueries } from "app/utils/routerUtils";
import { checkForSpecialOffer } from "app/pages/Booking/Payment/paymentSelector";
import get from "lodash/get";
import usePrintMode from "app/reducers/usePrintMode";
import { startPrint } from "app/reducers/printActionCreators";
import { fetchStopoverAccommodationDescription } from "app/pages/Stopover/stopoverActionCreators";
import { fetchHotelOnlyAccommodationDescription } from "app/pages/HotelOnly/Booking/pages/Quote/BookingQuoteActionCreator";
import { useHotelAccommodationDescription } from "app/features/hotel/hotelDescription/hooks/useHotelAccommodationDescription";
import { isHotelAccommodationDescriptionSuccess } from "app/features/hotel/hotelDescription/types";
import { mapAccommodationDescriptionToFicheProduit } from "app/features/hotel/hotelDescription/utils/mapAccommodationDescriptionToFicheProduit";
import { PRODUCT_PAGE_CODE_STATUS } from "app/constants";

const mapStateToProps = state => {
	return {
		hotel: state.ficheProduit,
		quotationCode: state.smartDP.code,
		hasSdpSpecialOffer: checkForSpecialOffer(state), // true si le back renvoi au moins un vol remboursable
		specialOfferInfos: state.ficheProduit.specialOffer,
		partnerCode: state.partner.code,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchSDPAccommodationDescription,
			fetchStopoverAccommodationDescription,
			fetchHotelOnlyAccommodationDescription,
			startPrint,
		},
		dispatch
	);
};

const SmartDPHotelDescriptionContainer = props => {
	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

	const { quotationCode, hotel: hotelFromStore, partnerCode } = stateProps;
	const { isStopover, isHotelOnly } = props;
	const {
		fetchSDPAccommodationDescription: fetchSDPAccommodationDescriptionProp,
		fetchStopoverAccommodationDescription: fetchStopoverAccommodationDescriptionProp,
		fetchHotelOnlyAccommodationDescription: fetchHotelOnlyAccommodationDescriptionProp,
		startPrint: startPrintProp,
	} = dispatchProps;

	const params = useParams();
	const location = useLocation();

	const isPrintMode = usePrintMode();

	// Extract only the params needed for API calls
	const query = parseQueries(location.search);
	const accommodationIdFromQuery = query.accommodationId;
	const stopoverDuration = query.stopoverDuration;
	const codeFromState = location.state?.code;

	// Compute final values sent to API (not individual sources)
	const { accommodationId: accommodationIdFromParam, code: paramCode } = params;
	const accommodationIdForPayload = accommodationIdFromParam || accommodationIdFromQuery;
	const codeForPayload = codeFromState || paramCode || quotationCode;

	// ne requête que sans bookingFlowId : avec un code, l'appel Redux ci-dessous prend le relais.
	// Désactivée, la query sert quand même la fiche déjà en cache (SSR) pour éviter un loader
	const isSDP = !isStopover && !isHotelOnly;
	const { data: prefetchedHotel } = useHotelAccommodationDescription(
		isSDP ? accommodationIdForPayload : undefined,
		"sdp",
		partnerCode,
		{ enabled: !codeForPayload }
	);

	const isHotelFromStorePending =
		!hotelFromStore?.status || hotelFromStore.status === PRODUCT_PAGE_CODE_STATUS.LOADING;
	// comme ficheProduitReducer : une réponse sans status est un succès
	const isPrefetchedHotelAvailable =
		Boolean(prefetchedHotel) &&
		(!prefetchedHotel.status || isHotelAccommodationDescriptionSuccess(prefetchedHotel));
	const shouldUsePrefetchedHotel = isHotelFromStorePending && isPrefetchedHotelAvailable;

	const hotel = shouldUsePrefetchedHotel
		? mapAccommodationDescriptionToFicheProduit(prefetchedHotel)
		: hotelFromStore;

	useEffect(() => {
		if (isPrintMode) {
			startPrintProp();
		}
	}, [isPrintMode]);

	useEffect(() => {
		if (!isHotelOnly && codeForPayload && accommodationIdForPayload) {
			if (isStopover) {
				fetchStopoverAccommodationDescriptionProp({
					bookingFlowId: codeForPayload,
					accommodationId: accommodationIdForPayload,
					stopoverDuration,
				});
			} else {
				fetchSDPAccommodationDescriptionProp({
					code: codeForPayload,
					accommodationId: accommodationIdForPayload,
				});
			}
		} else if (isHotelOnly && codeForPayload && accommodationIdForPayload) {
			fetchHotelOnlyAccommodationDescriptionProp({
				id: parseInt(accommodationIdForPayload, 10),
				bookingFlowId: get(location, "state.code") || codeForPayload,
			});
		}
	}, [accommodationIdForPayload, codeForPayload, stopoverDuration, isStopover, isHotelOnly]);

	return (
		<SmartDPHotelDescription
			{...props}
			{...stateProps}
			{...dispatchProps}
			hotel={hotel}
			specialOfferInfos={hotel.specialOffer}
		/>
	);
};

SmartDPHotelDescriptionContainer.propTypes = {
	isStopover: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
};

export default SmartDPHotelDescriptionContainer;
